import { readEventSources } from "@/lib/event-sources";
import type { IEventSource } from "@/types/event";
import type {
  ProposedChange,
  RefreshChangeDecision,
  RefreshDuplicateAction,
  RefreshLineupDiff,
  RefreshLineupNameDecision,
  RefreshPageGoneAction,
  RefreshReviewProblem,
  RefreshVenueAction,
} from "@/types/tina";

/** The `RemoteSelect` option shape for a venue search hit — mirrors `venue-resolution.tsx`'s. */
export interface VenueOption {
  id: string;
  name: string;
  address?: string;
  media?: { photos?: Array<{ url: string }> };
}

/**
 * One curator's in-progress verdict on one `ProposedChange`, keyed by its
 * index in `problem.proposals` (a `kind` is not guaranteed unique across the
 * array, so the index — not the kind — is the stable key).
 */
export interface ProposalDecision {
  /** `null` = not yet answered. Submit stays disabled until every proposal is `true`/`false`. */
  accept: boolean | null;
  /** Set only once the curator types into the value input; `undefined` = unedited. */
  editedValue?: string;
  /** `kind === "venue"` only — the id `refresh_apply` needs to resolve the venue. */
  venueId?: string;
  /** The full picked option, kept only so `RemoteSelect` can render the selection. */
  venueOption?: VenueOption;
  /**
   * `kind === "venue"` only — the curator's venue action (2026-09-11): a pick, a create-from-Maps
   * or a use-Maps-as-pin, exactly as the scrape venue HITL sends them. Set only once the fields
   * behind it are valid (an id picked, a real Google Maps URL typed), so a set action is a
   * writable one. `venueId` is kept in step with a pick for the legacy `value` on the wire.
   */
  venueAction?: RefreshVenueAction;
  /**
   * `kind === "lineup"` only — the curator's tick per delta, keyed by
   * {@link lineupDeltaKey}.
   *
   * **Sparse on purpose, and never pre-filled.** A key is written only when the curator
   * actually clicks that row's checkbox (or "Accept all", which writes every key); a
   * missing key means "still at its default", which {@link defaultLineupTick} answers.
   * Seeding the map with the defaults at mount would work too, but it would make
   * "untouched" and "deliberately set back to the default" indistinguishable, and it would
   * need a `useEffect` to re-seed whenever the diff changed identity.
   */
  lineupTicks?: Record<string, boolean>;
}

export type ProposalDecisionMap = Record<number, ProposalDecision>;

/** Which side of the lineup diff a row came from. Mirrors the wire's `action`. */
export type LineupAction = RefreshLineupNameDecision["action"];

/** One tickable row in the lineup card: a name plus the side it came from. */
export interface LineupDelta {
  name: string;
  action: LineupAction;
}

/**
 * The tickable rows of a lineup diff — every addition, then every removal.
 *
 * Additions first because that is the order the owner's mock shows, and because it puts
 * the safe, default-ticked rows above the destructive opt-in ones rather than interleaving
 * the two. `unchanged` is deliberately **not** here: those names are on both sides, there
 * is nothing to decide about them, and a tick against one would have no `action` to carry.
 *
 * Returns `[]` for an absent or null diff, which is what drives the whole degradation
 * path: no deltas means no per-name list on the wire, which means the backend's legacy
 * "apply every addition, remove nobody" behaviour — exactly what this panel did before.
 */
export function lineupDeltas(
  diff: RefreshLineupDiff | null | undefined,
): LineupDelta[] {
  if (!diff) return [];
  return [
    ...diff.additions.map((name): LineupDelta => ({ name, action: "add" })),
    ...diff.removals.map((name): LineupDelta => ({ name, action: "remove" })),
  ];
}

/**
 * The `lineupTicks` key for one delta.
 *
 * Keyed by `action` *and* `name`, not by name alone. A set difference cannot put the same
 * name on both sides today, so a bare name would be unique in practice — but the tick map
 * is the thing that decides whether a performer is added or deleted from a live event, and
 * "correct because the producer happens not to emit that" is the wrong guarantee to rest
 * that on.
 */
export function lineupDeltaKey(delta: LineupDelta): string {
  return `${delta.action}:${delta.name}`;
}

/**
 * The tick a row starts on, decided by the product owner: **additions ticked, removals
 * unticked.**
 *
 * The asymmetry is the whole point of the design, not an oversight. An addition is
 * additive to a live published event and cheap to undo; a removal deletes a performer
 * from an event people have already saved, so it is opt-in — the curator has to positively
 * say yes to each one. A default-ticked removal would turn one distracted "Accept" into a
 * silent deletion, which is the failure mode the per-name path exists to prevent.
 */
export function defaultLineupTick(action: LineupAction): boolean {
  return action === "add";
}

/** One row's current tick: the curator's if they touched it, otherwise the default. */
export function lineupTickAt(
  decision: ProposalDecision,
  delta: LineupDelta,
): boolean {
  return (
    decision.lineupTicks?.[lineupDeltaKey(delta)] ??
    defaultLineupTick(delta.action)
  );
}

export function decisionAt(
  decisions: ProposalDecisionMap,
  index: number,
): ProposalDecision {
  return decisions[index] ?? { accept: null };
}

/** How many proposals still have no accept/reject verdict. */
export function countUnanswered(
  proposals: ProposedChange[],
  decisions: ProposalDecisionMap,
): number {
  return proposals.filter((_, i) => decisionAt(decisions, i).accept === null)
    .length;
}

/**
 * Whether every accepted `venue` proposal has a chosen venue id.
 *
 * `refresh_apply` resolves the venue by id to build the PATCH's venue DTO,
 * geodata, geolocation and timezone — an accepted venue decision with no id
 * cannot be written, so Submit must stay blocked on it exactly like the
 * unanswered-proposals gate.
 */
export function acceptedVenuesResolved(
  proposals: ProposedChange[],
  decisions: ProposalDecisionMap,
): boolean {
  return proposals.every((proposal, i) => {
    if (proposal.kind !== "venue") return true;
    const decision = decisionAt(decisions, i);
    return (
      decision.accept !== true ||
      Boolean(decision.venueAction) ||
      Boolean(decision.venueId)
    );
  });
}

/**
 * Submit is enabled only once every proposal is answered, every accepted
 * venue proposal has a venue id, and — on a `page_gone` park — an action is
 * chosen, and — on a duplicate card — a keep/not-duplicate choice is made. The gates are independent: a payload can carry both
 * proposals and `page_gone` (the schema permits it), and all apply together.
 */
export function canSubmitRefreshResolution(
  problem: RefreshReviewProblem,
  decisions: ProposalDecisionMap,
  pageGoneAction: RefreshPageGoneAction | null,
  duplicateAction: RefreshDuplicateAction | null,
): boolean {
  const proposals = problem.proposals;
  return (
    countUnanswered(proposals, decisions) === 0 &&
    acceptedVenuesResolved(proposals, decisions) &&
    (!problem.page_gone || pageGoneAction !== null) &&
    // Ruling R10: a duplicate card is answered before Submit, like page-gone.
    (!problem.duplicate_of || duplicateAction !== null)
  );
}

/**
 * One `RefreshChangeDecision` per proposal, in the proposals' order — the
 * shape `POST /hitl/{id}/resolve` expects for `RefreshReviewResolution.decisions`.
 *
 * Omitting `value` on an unedited accept means "take `proposal.new` verbatim";
 * sending it — even if a curator retyped the identical text — means "I edited
 * it". So presence of the key is driven by whether `editedValue` was ever set
 * by an onChange, not by comparing the current text to `proposal.new`.
 *
 * ## The `lineup` list, and why it is built from the diff
 *
 * A `lineup` proposal accepted against a park that carries a `lineup_diff` also sends
 * per-name decisions. `RefreshChangeDecision.lineup` is **exhaustive when non-empty**:
 * tina applies exactly its `accept: true` entries and nothing else, so an addition that
 * is merely *missing* from a non-empty list is silently not applied.
 *
 * That is why this maps over `lineupDeltas(problem.lineup_diff)` — every delta — and
 * stamps each one's current tick, rather than filtering to the ticked rows and sending a
 * short list. Sending only the ticked rows would be a smaller payload that quietly drops
 * the unticked additions, i.e. strictly worse than the legacy path it replaces. The
 * unticked rows are load-bearing: they are the "no" half of an exhaustive answer.
 *
 * Three cases collapse onto the legacy path, all by producing no `lineup` key at all:
 *
 * - the park carries no `lineup_diff` (an older tina, or the backend half not yet
 *   deployed) — `lineupDeltas` returns `[]`;
 * - it carries one with no additions and no removals — nothing to say, and an empty list
 *   means the same thing as an absent one to the backend;
 * - the curator rejected the proposal — a rejected change applies nothing whatever the
 *   ticks said, and per-name verdicts on a change nobody accepted are noise that tina's
 *   decision memory should not be asked to interpret.
 *
 * `value` and `lineup` are mutually exclusive on a lineup proposal by construction: when
 * there are deltas the panel renders ticks instead of the free-text value input, so
 * `editedValue` cannot have been set, and the exhaustive list is the only instruction.
 */
export function buildRefreshDecisions(
  problem: RefreshReviewProblem,
  decisions: ProposalDecisionMap,
): RefreshChangeDecision[] {
  const deltas = lineupDeltas(problem.lineup_diff);
  return problem.proposals.map((proposal, i) => {
    const decision = decisionAt(decisions, i);
    const accept = decision.accept === true;
    const entry: RefreshChangeDecision = { kind: proposal.kind, accept };
    if (accept && proposal.kind === "lineup" && deltas.length > 0) {
      entry.lineup = deltas.map((delta) => ({
        name: delta.name,
        action: delta.action,
        accept: lineupTickAt(decision, delta),
      }));
    } else if (accept && proposal.kind === "venue") {
      // The typed action is the instruction; `value` rides along for a pick so a tina that
      // predates `venue` still reads the same decision (2026-09-11).
      const action =
        decision.venueAction ??
        (decision.venueId
          ? ({ kind: "venue.pick", venue_id: decision.venueId } as const)
          : undefined);
      if (action) entry.venue = action;
      if (action?.kind === "venue.pick") entry.value = action.venue_id;
    } else if (
      accept &&
      proposal.kind !== "venue" &&
      decision.editedValue !== undefined
    ) {
      entry.value = decision.editedValue;
    }
    return entry;
  });
}

export function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * The sightings that are **not** the page under review (owner ruling R76).
 *
 * "Also seen on" is meant to answer "what else has said this", and the page the curator
 * is currently reviewing is not an answer to that. On the live park that prompted this,
 * the source under review was `nitbcn` and the list read `nitbcn · ra.co` — so the page
 * being decided on was presented as if it were independent corroboration of itself.
 *
 * The comparison goes through {@link sameSourcePage} rather than `===` because the two
 * strings come from different writers: `problem.source_url` is `events.url` verbatim
 * (`stages/apply.py`), while a `sources` entry is whatever the duplicate hook recorded
 * for that page — the same URL can differ by scheme, `www.`, a trailing slash or a
 * tracking query.
 *
 * Returns `[]` for a park that records no sources at all, which the list renders as
 * nothing — the same rule an event with a single known page has always followed.
 */
export function otherSightings(
  sources: unknown,
  sourceUrl: string,
): IEventSource[] {
  return readEventSources(sources).filter(
    (entry) => !sameSourcePage(entry.url, sourceUrl),
  );
}

/**
 * Whether two recorded URLs point at the same page, for de-duplication only.
 *
 * Deliberately lenient — scheme, `www.`, a trailing slash, the query string and the
 * fragment are all ignored — because the alternative failure is the one this exists to
 * fix: showing the page under review as a second, independent sighting. Being slightly
 * too eager here hides a row that is a duplicate of a link the curator already has;
 * being too strict puts a lie back on the screen.
 *
 * Unparseable input falls back to a trimmed, lower-cased string compare rather than
 * throwing inside a render.
 */
export function sameSourcePage(a: string, b: string): boolean {
  return canonicalSourcePage(a) === canonicalSourcePage(b);
}

function canonicalSourcePage(url: string): string {
  const trimmed = url.trim();
  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const path = parsed.pathname.replace(/\/+$/, "");
    return `${host}${path}`.toLowerCase();
  } catch {
    return trimmed.toLowerCase().replace(/\/+$/, "");
  }
}

/**
 * A `poster` proposal's `new` read as a 1-based candidate position, or `null` when it is
 * not one (owner ruling R76).
 *
 * `ProposedChange.new` is a string for every kind, and for `poster` the compare model
 * fills it with **which candidate it picked**, not a URL — the live park read
 * `old: null, new: "1"`, which the generic old→new line rendered as "(empty) → 1". A bare
 * `1` beside two images is worse than no label at all, so the position is read out here
 * and rendered against the candidate it names.
 *
 * `null` for anything that is not a plain positive integer: some parks carry the page's
 * image URL in `new` instead, and that is evidence to leave alone rather than a number to
 * misread.
 */
export function posterCandidatePosition(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const position = Number(value.trim());
  return Number.isInteger(position) && position >= 1 ? position : null;
}

/**
 * A Google Maps search for a `venue` proposal's page text, so a curator can check
 * where the proposed venue really is before picking or creating it (curator ask,
 * 2026-10-02). The city is appended only when the text does not already name it:
 * a bare venue name like "Gagarin" finds the wrong place without one. Null when
 * there is no text to search for.
 */
export function venueMapsSearchUrl(
  venueText: string | null | undefined,
  city?: string | null,
): string | null {
  const text = venueText?.trim();
  if (!text) return null;
  const cityName = city?.trim();
  const query =
    cityName && !text.toLowerCase().includes(cityName.toLowerCase())
      ? `${text}, ${cityName}`
      : text;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
